import { useCallback, useMemo, useState } from 'react';
import type { MeshNode } from '../types';

/**
 * Client-side table state for the mesh nodes list.
 *
 * Mirrors the shared useClientSideTable, with one addition required by the
 * DataTable multi-select filter cells: select filters store a JSON array of
 * chosen option values (e.g. '["online"]'), so matching must parse it and test
 * membership — plain substring matching never hits (same pattern as
 * useReceivingListTable / useSkuListTable / useQmsTicketListTable).
 */

function matchesColumnFilter(value: unknown, filterValue: string): boolean {
  if (!filterValue) return true;
  if (filterValue.startsWith('[')) {
    try {
      const selected = JSON.parse(filterValue) as string[];
      if (selected.length === 0) return true;
      return selected.includes(String(value ?? ''));
    } catch {
      // Malformed payload — fall through to substring matching.
    }
  }
  if (value === null || value === undefined) return false;
  return String(value).toLowerCase().includes(filterValue.toLowerCase());
}

function compare(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function useMeshTable({
  items,
  columns,
  initialColumnFilters = {},
  initialSortKey = 'status',
  initialSortDirection = -1,
}: {
  items: MeshNode[];
  columns: Array<{ key: string; getValue: (item: MeshNode, ctx?: unknown) => string | number | null }>;
  initialColumnFilters?: Record<string, string>;
  initialSortKey?: string;
  initialSortDirection?: 1 | -1;
}) {
  const [sortKey, setSortKey] = useState(initialSortKey);
  const [sortDirection, setSortDirection] = useState<1 | -1>(initialSortDirection);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(initialColumnFilters);

  const getColumnValue = useCallback(
    (item: MeshNode, key: string) => columns.find((c) => c.key === key)?.getValue(item),
    [columns],
  );

  const setSort = useCallback((key: string) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDirection((d) => (d === 1 ? -1 : 1));
        return prev;
      }
      setSortDirection(1);
      return key;
    });
  }, []);

  const setColumnFilter = useCallback((key: string, value: string) => {
    setColumnFilters((prev) => {
      if (!value) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: value };
    });
  }, []);

  const clear = useCallback(() => setColumnFilters({}), []);

  const sortedItems = useMemo(() => {
    const activeFilters = Object.entries(columnFilters);
    const filtered = items.filter((item) => {
      for (const [key, filterValue] of activeFilters) {
        if (!matchesColumnFilter(getColumnValue(item, key), filterValue)) return false;
      }
      return true;
    });
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => (
      compare(getColumnValue(a, sortKey), getColumnValue(b, sortKey)) * sortDirection
    ));
  }, [items, columnFilters, sortKey, sortDirection, getColumnValue]);

  const isFiltered = Object.keys(columnFilters).length > 0;

  return {
    sortedItems,
    total: sortedItems.length,
    sortKey,
    sortDirection,
    setSort,
    columnFilters,
    setColumnFilter,
    isFiltered,
    clear,
  };
}
